import * as React from 'react'
import { CastingStore } from '../../../../services/stores/casting.store'
import { I18nService } from '../../../../services/utils/i18n.service'
import { Input } from '../casting-styled.component'
import { castingGeodingSendQuery } from '../../../../services/utils/casting-geocoding.service'
import { useDebouncer } from '../../../../hooks/use-debouncer'
import { useNonConcurrentPromiseProxy } from '../../../../hooks/use-non-concurrent-promise-proxy'
import { Select } from '../../../shared/select.component'
import { countryIsFrance } from '../../../../services/utils/country.service'

interface Props {
  mode:  'mobile-inside-tooltip' | 'legacy-step-1'
  inputClassName?: string
  hideCountrySelectDespiteWorldwide?: boolean
  rawInput?: string
  hasErrors?: boolean
  onFocus?: () => void
  style?: React.CSSProperties
  data: CastingStore['data']
  store: CastingStore
}

const i18nScoped = new I18nService(require('./casting-city-input.component.yml'))

function CastingCityInputComponent ({ store, data: { market, city, countries, country }, mode, inputClassName, hasErrors, onFocus, style, hideCountrySelectDespiteWorldwide }: Props, ref: React.Ref<HTMLInputElement>) {
  const rawInputFromProps = React.useMemo(() => {
    if (market.field_for_geocoding === 'zipcode') return city?.postalCode || city?.content
    else if (market.field_for_geocoding === 'postaladdress') return city?.content
    else return city?.city
  }, [city?.city, city?.content, city?.postalCode, market.field_for_geocoding])

  const [rawInput, setRawInput] = React.useState(rawInputFromProps || '')
  React.useEffect(() => { setRawInput(rawInputFromProps || '') }, [rawInputFromProps])

  // sendQueryRef calls are debounced to avoid sending too many requests (max 1 per second) and concurrency is limitted to 1 (the previous request is cancelled if a new one is sent)
  const debouncer = useDebouncer(window.env === 'test' ? 0 : 1000)
  const loadConcurrentProxy = useNonConcurrentPromiseProxy()
  const sendQueryRef = React.useRef((input: string, country: string) => castingGeodingSendQuery(input, market, country).then(cityResults => store.set({ cityResults })))

  const onQueryChange = React.useCallback((input: string) => {
    if (input.length) {
      setRawInput(input)

      if (country && countryIsFrance(country) && market.coverage !== 'worldwide') {
        const wantedCountry = ({ '971': 'GP', '972': 'MQ', '973': 'GF', '974': 'RE', '976': 'YT', '988': 'NC' } as Record<string, string>)[input.substring(0, 3)] || market.country
        if (country !== wantedCountry) store.set({ country: wantedCountry })
      }

      if (input.length > 2) {
        store.set({ cityResults: { status: 'loading', data: [] } })
        debouncer(() => loadConcurrentProxy(sendQueryRef.current(input, store.data.country)))
      }
    } else {
      setRawInput(input)
      store.set({ cityResults: null })
    }
  }, [country, market.coverage, market.country, store, debouncer, loadConcurrentProxy])

  const inputProps: React.InputHTMLAttributes<HTMLInputElement> & { $hasErrors: boolean } = {
    type: 'text',
    value: rawInput,
    placeholder: i18nScoped.t('long.' + market.field_for_geocoding),
    onChange: (evt: any) => onQueryChange(evt.target.value),
    onFocus,
    autoComplete: 'off',
    id: 'js-test-casting-city-input',
    $hasErrors: hasErrors,
    style
  }

  if (mode === 'mobile-inside-tooltip' || mode === 'legacy-step-1') {
    return <>
      { !hideCountrySelectDespiteWorldwide && market.coverage === 'worldwide' && <div style={{ marginBottom: '0.75em' }}>
        <Select
          id="casting-country"
          placeholder={ i18nScoped.t('long.country') }
          value={ countries.find(_ => _.id === country) }
          options={ countries }
          getOptionLabel={ val => val.text }
          getOptionValue={ val => val.id }
          onChange={ val => store.set({ country: val.id, city: null }) }
        />
      </div> }
      <div className={ inputClassName || '' }>
        <Input { ...inputProps } autoFocus={ mode === 'mobile-inside-tooltip' || undefined } ref={ ref } />
      </div>
    </>
  } else {
    return null
  }
}

export default React.forwardRef(CastingCityInputComponent)